// AGC: DashboardLotsSection - Horizontal scrolling mini LOD cards
// AGC: Shows quick overview of lots with swipe navigation
// AGC: DJI Action 5 Pro inspired horizontal carousel
// AGC: Now shows weather info (temperature + rain 24h) instead of health indicators

import React, { useState, useEffect, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { LoteCardMini } from './LoteCardMini'
import { useLotes, useFarms } from '../../context'
import { useLotCurrentWeather } from '../../hooks/useLotCurrentWeather'
import { getCropDisplayInfo } from '../../types/field'
import { getCropImageUrl } from '../../config/localCropImages'
import { getOrFetchCropImage } from '../../services/cropImageCacheService'
import type { Plot } from '../../types'

interface DashboardLotsSectionProps {
  onLotClick: (lotId: string) => void
  onViewAllClick: () => void
  // AGC: compact=true on desktop: removes top padding (alignment) + smaller cards (3 fit in half-width)
  compact?: boolean
}

/**
 * AGC: Dashboard Lots Section
 * AGC: Features:
 * AGC: - Horizontal scroll container
 * AGC: - Mini square LOD cards (160x160px)
 * AGC: - Smooth momentum scrolling
 * AGC: - Snap scrolling on mobile
 * AGC: - Shows first 10 lots
 * AGC: - Weather info (temperature + rain 24h)
 */
export const DashboardLotsSection: React.FC<DashboardLotsSectionProps> = ({
  onLotClick,
  onViewAllClick,
  compact = false,
}) => {
  const { plots } = useLotes()
  const { currentFarm } = useFarms()
  
  // AGC: Show first 10 lots sorted by name; dedupe by id to avoid React duplicate key warnings
  const displayLots = useMemo(() => {
    if (!plots || plots.length === 0) return []
    const unique = Array.from(new Map(plots.map(p => [p.id, p])).values())
    const sorted = [...unique].sort((a, b) => a.name.localeCompare(b.name))
    return sorted.slice(0, 10)
  }, [plots])
  
  if (!plots || plots.length === 0) {
    return null // AGC: Hide section if no lots
  }
  
  return (
    // AGC: compact mode (desktop): no top padding so Lotes header aligns with Atividades Recentes header
    <section className={compact ? 'pb-2' : 'py-4'}>
      {/* AGC: Section Header */}
      <div className={`${compact ? 'px-0 mb-2' : 'px-4 mb-3'} flex items-center justify-between`}>
        <h3 className="text-gray-900 dark:text-white font-semibold text-sm">
          Lotes
        </h3>
        <button
          onClick={onViewAllClick}
          className="flex items-center gap-1 text-sm text-[#065F46] hover:text-[#047857] font-medium transition-colors"
        >
          Ver todos
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* AGC: Horizontal Scroll Container */}
      <div
        className={`overflow-x-auto overflow-y-visible scrollbar-hide scroll-smooth ${compact ? 'px-0' : 'px-4'}`}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* AGC: compact gap-2 to give 3 cards more room */}
        <div className={`flex py-2 ${compact ? 'gap-2' : 'gap-3'}`}>
          {displayLots.map((lot) => (
            <LoteCardMiniWrapper
              key={lot.id}
              lot={lot}
              onClick={() => onLotClick(lot.id)}
              compact={compact}
              farmCoords={currentFarm?.coordinates as [number, number] | undefined}
            />
          ))}
        </div>
      </div>

    </section>
  )
}

/**
 * AGC: Wrapper component that handles image loading for mini cards
 * AGC: Same logic as LotesList but for mini cards
 */
interface LoteCardMiniWrapperProps {
  lot: Plot
  onClick: () => void
  compact?: boolean
  farmCoords?: [number, number]
}

const LoteCardMiniWrapper: React.FC<LoteCardMiniWrapperProps> = ({ lot, onClick, compact = false, farmCoords }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isImageLoading, setIsImageLoading] = useState(true)
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const weather = useLotCurrentWeather(lot, farmCoords)
  
  // AGC: Load crop image with priority: Admin upload > Local test image > null
  useEffect(() => {
    let mounted = true
    
    async function loadImage() {
      // AGC: Priority 1 - Try admin-uploaded image (Firebase or manual URL)
      if (cropDisplay.imageUrl) {
        try {
          const cachedUrl = await getOrFetchCropImage(
            lot.cropType,
            cropDisplay.imageUrl,
            cropDisplay.imageUpdatedAt
          )
          
          if (mounted) {
            setImageUrl(cachedUrl)
            setIsImageLoading(false)
          }
          return // AGC: Success, stop here
        } catch (error) {
          console.warn(`⚠️ AGC: Failed to load admin image for ${lot.cropType}:`, error)
          // AGC: Fall through to local image
        }
      }
      
      // AGC: Priority 2 - Try local testing image (from /public/crop-images/)
      const localImageUrl = getCropImageUrl(lot.cropType, null)
      if (localImageUrl) {
        console.log(`🖼️ AGC: Using local test image for ${lot.cropType}: ${localImageUrl}`)
        if (mounted) {
          setImageUrl(localImageUrl)
        }
      }
      
      // AGC: Done loading (whether we found an image or not)
      if (mounted) {
        setIsImageLoading(false)
      }
    }
    
    loadImage()
    
    return () => {
      mounted = false
      // AGC: Only revoke blob URLs (from cache), not public URLs
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [lot.cropType, cropDisplay.imageUrl, cropDisplay.imageUpdatedAt])
  
  return (
    <LoteCardMini
      lot={lot}
      onClick={onClick}
      imageUrl={imageUrl}
      isImageLoading={isImageLoading}
      compact={compact}
      weather={weather}
    />
  )
}
